<!-- ABOUTME: Email templates list component with responsive flex layout -->
<!-- ABOUTME: Modern design using Tailwind CSS utility classes and Lucide Vue icons with toggle switch and dropdown menu -->
<template>
  <div class="min-h-screen">
    <!-- Header Section -->
    <header class="py-5 px-5">
      <div class="max-w-7xl">
        <div class="flex flex-col gap-4">
          <div class="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 class="text-3xl font-bold text-title-dark m-0">Email Templates</h1>
              <p class="text-text-dark text-sm mt-1 mb-0">Reusable email templates for automation rules</p>
            </div>

            <div class="flex flex-wrap gap-2">
              <a
                :href="newTemplatePath"
                class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md no-underline font-medium text-sm transition-opacity hover:opacity-90">
                <Plus :size="16" class="mr-2" />
                New Template
              </a>

              <button
                @click="toggleArchived"
                class="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-text-dark rounded-md font-medium text-sm cursor-pointer transition-colors hover:bg-gray-50">
                <Archive :size="16" class="mr-2" />
                {{ showArchived ? 'Show Active' : 'Show Archived' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="max-w-7xl py-6 px-5">

      <!-- Templates List -->
      <div v-if="templates.length > 0" class="flex flex-col gap-2">
        <div
          v-for="template in templates"
          :key="template.id"
          class="bg-white rounded-md border border-gray-200 py-2.5 px-4 transition-colors hover:border-primary">

          <!-- Single Row Layout -->
          <div class="flex items-center justify-between gap-4 flex-wrap">

            <!-- Left: Title & Meta -->
            <div class="flex items-center gap-4 flex-1 min-w-0">
              <!-- Clickable Template Key -->
              <a
                :href="getEditPath(template.id)"
                class="text-[15px] font-semibold text-title-dark m-0 overflow-hidden text-ellipsis whitespace-nowrap no-underline hover:text-primary transition-colors cursor-pointer">
                {{ template.template_key }}
              </a>

              <!-- Title (if exists) -->
              <span v-if="template.title" class="text-gray-400 text-[13px] flex-shrink-0 hidden sm:inline">
                {{ template.title }}
              </span>

              <!-- Date -->
              <span class="text-gray-400 text-[13px] flex-shrink-0 hidden md:inline">
                {{ formatDate(template.created_at) }}
              </span>

              <!-- Languages -->
              <div class="hidden lg:flex items-center gap-1.5">
                <Globe :size="14" :stroke-width="2" class="text-gray-400" />
                <span class="text-text-dark text-[13px]">
                  {{ template.languages.length > 0 ? template.languages.join(', ') : 'No variants' }}
                </span>
              </div>

              <!-- Automation Rules Badge -->
              <span
                v-if="template.automation_rules_count > 0"
                class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex-shrink-0"
                :title="`Used by ${template.automation_rules_count} automation rule(s)`">
                <Zap :size="12" :stroke-width="2" class="mr-1" />
                {{ template.automation_rules_count }}
              </span>
            </div>

            <!-- Right: Toggle Switch & Actions -->
            <div class="flex items-center gap-3 flex-shrink-0">
              <!-- Active/Draft Toggle Switch -->
              <div v-if="template.status !== 'archived'" class="flex items-center gap-2">
                <span class="text-xs text-gray-500">Draft</span>
                <button
                  @click="toggleStatus(template)"
                  :disabled="togglingStatus[template.id]"
                  :class="[
                    'relative inline-flex h-6 w-11 items-center rounded-full transition-colors',
                    template.status === 'active' ? 'bg-green-500' : 'bg-gray-300',
                    togglingStatus[template.id] ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                  ]"
                  class="border-0">
                  <span
                    :class="[
                      'inline-block h-4 w-4 transform rounded-full bg-white transition-transform',
                      template.status === 'active' ? 'translate-x-6' : 'translate-x-1'
                    ]"
                  ></span>
                </button>
                <span class="text-xs text-gray-500">Active</span>
              </div>

              <!-- Dropdown Menu -->
              <div class="relative">
                <button
                  @click="toggleDropdown(template.id)"
                  class="inline-flex items-center justify-center w-8 h-8 text-text-dark rounded-md cursor-pointer transition-colors hover:bg-gray-100 border-0 bg-transparent">
                  <MoreVertical :size="18" :stroke-width="2" />
                </button>

                <!-- Dropdown Items -->
                <div
                  v-if="activeDropdown === template.id"
                  class="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-50">
                  <div class="py-1">
                    <a
                      :href="getViewPath(template.id)"
                      class="flex items-center px-4 py-2 text-sm text-text-dark hover:bg-gray-50 no-underline transition-colors">
                      <Eye :size="14" :stroke-width="2" class="mr-2" />
                      View
                    </a>

                    <a
                      :href="getEditPath(template.id)"
                      class="flex items-center px-4 py-2 text-sm text-text-dark hover:bg-gray-50 no-underline transition-colors">
                      <Pencil :size="14" :stroke-width="2" class="mr-2" />
                      Edit
                    </a>

                    <button
                      v-if="template.status !== 'archived'"
                      @click="confirmArchive(template.id, template.template_key)"
                      class="flex items-center w-full px-4 py-2 text-sm text-text-dark hover:bg-gray-50 border-0 bg-transparent text-left cursor-pointer transition-colors">
                      <Archive :size="14" :stroke-width="2" class="mr-2" />
                      Archive
                    </button>

                    <button
                      v-if="canDelete(template)"
                      @click="confirmDelete(template.id, template.template_key, template.automation_rules_count)"
                      class="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 border-0 bg-transparent text-left cursor-pointer transition-colors">
                      <Trash2 :size="14" :stroke-width="2" class="mr-2" />
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-else class="bg-white rounded-lg border border-gray-200 p-12 text-center">
        <FileText :size="64" :stroke-width="1.5" class="text-gray-300 mx-auto mb-4 block" />
        <p class="text-gray-500 text-base">No email templates found.</p>
        <a
          :href="newTemplatePath"
          class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md no-underline font-medium text-sm mt-4 transition-opacity hover:opacity-90">
          <Plus :size="16" class="mr-2" />
          Create your first template
        </a>
      </div>
    </main>
  </div>
</template>

<script>
import { Plus, Archive, Globe, Eye, Pencil, Trash2, FileText, Zap, MoreVertical } from 'lucide-vue-next'
import axios from 'axios'

export default {
  name: 'EmailTemplatesList',

  components: {
    Plus,
    Archive,
    Globe,
    Eye,
    Pencil,
    Trash2,
    FileText,
    Zap,
    MoreVertical
  },

  props: {
    templates: {
      type: Array,
      required: true
    },
    showArchived: {
      type: Boolean,
      default: false
    },
    indexPath: {
      type: String,
      required: true
    },
    newTemplatePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      activeDropdown: null,
      togglingStatus: {}
    }
  },

  mounted() {
    // Close dropdown when clicking outside
    document.addEventListener('click', this.handleClickOutside)
  },

  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside)
  },

  methods: {
    toggleDropdown(templateId) {
      this.activeDropdown = this.activeDropdown === templateId ? null : templateId
    },

    handleClickOutside(event) {
      // Close dropdown if clicking outside
      if (!event.target.closest('.relative')) {
        this.activeDropdown = null
      }
    },

    async toggleStatus(template) {
      // Prevent multiple simultaneous toggles
      if (this.togglingStatus[template.id]) return

      this.togglingStatus[template.id] = true

      try {
        const isActivating = template.status !== 'active'
        const endpoint = isActivating ? 'activate' : 'deactivate'

        await axios.post(`${this.indexPath}/${template.id}/${endpoint}`, {
          authenticity_token: document.querySelector('meta[name="csrf-token"]').content
        })

        // Update local template status
        template.status = isActivating ? 'active' : 'draft'

      } catch (error) {
        console.error('Error toggling template status:', error)
        alert(`Failed to ${template.status === 'active' ? 'deactivate' : 'activate'} template. Please try again.`)
      } finally {
        this.togglingStatus[template.id] = false
      }
    },
    formatDate(dateString) {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    getViewPath(id) {
      return `${this.indexPath}/${id}`
    },

    getEditPath(id) {
      return `${this.indexPath}/${id}/edit`
    },

    canDelete(template) {
      // Can only delete if no automation rules are using it
      return template.automation_rules_count === 0
    },

    toggleArchived() {
      const url = this.showArchived
        ? this.indexPath
        : `${this.indexPath}?show_archived=true`
      window.location.href = url
    },

    confirmDelete(id, templateKey, rulesCount) {
      let message = `Permanently delete template "${templateKey}"?`

      if (rulesCount > 0) {
        message = `WARNING: This template is used by ${rulesCount} active automation rule(s). It CANNOT be deleted until those rules are deactivated. Archive it instead?`
      }

      if (confirm(message)) {
        this.deleteTemplate(id)
      }
    },

    deleteTemplate(id) {
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = `${this.indexPath}/${id}`

      const methodInput = document.createElement('input')
      methodInput.type = 'hidden'
      methodInput.name = '_method'
      methodInput.value = 'delete'

      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrfToken

      form.appendChild(methodInput)
      form.appendChild(tokenInput)
      document.body.appendChild(form)
      form.submit()
    },

    confirmArchive(id, templateKey) {
      if (confirm(`Archive template "${templateKey}"?`)) {
        this.archiveTemplate(id)
      }
    },

    archiveTemplate(id) {
      const form = document.createElement('form')
      form.method = 'POST'
      form.action = `${this.indexPath}/${id}/archive`

      const csrfToken = document.querySelector('meta[name="csrf-token"]').content
      const tokenInput = document.createElement('input')
      tokenInput.type = 'hidden'
      tokenInput.name = 'authenticity_token'
      tokenInput.value = csrfToken

      form.appendChild(tokenInput)
      document.body.appendChild(form)
      form.submit()
    }
  }
}
</script>
